/**
 * The four headline metrics.
 *
 * Two deliberate departures from the old page:
 *
 * 1. The big figure is *this cycle*, not all-time. All-time totals never change
 *    a decision — they only ever grow — so they sit underneath as context.
 * 2. A tile is a control. Selecting one drives the chart below it, which spares
 *    the page a separate metric picker and makes the obvious gesture (click the
 *    number you care about) the working one.
 */
import { Cpu, FileStack, FileText, Layers } from 'lucide-react'
import { CountUp, Delta, HelpTip, Sparkline } from './BillingPrimitives'
import { billingMonthTooltip, formatCompact, formatCycleLabel, formatInt } from '../../utils/billing'

export const METRICS = [
  {
    key: 'pages_processed',
    label: 'Pages',
    icon: FileText,
    seriesIndex: 0,
    help: 'Pages put through extraction. This is the unit your plan allowance and any overage are measured in.',
  },
  {
    key: 'documents_processed',
    label: 'Documents',
    icon: FileStack,
    seriesIndex: 3,
    help: 'Files received and stored. A single document can be many pages, so this will not match the page count.',
  },
  {
    key: 'requests',
    label: 'Requests',
    icon: Layers,
    seriesIndex: 1,
    help: 'Extraction requests raised, whether submitted by a user, the Outlook add-in, an inbox or the API.',
  },
  {
    key: 'ai_tokens_used',
    label: 'AI tokens',
    icon: Cpu,
    seriesIndex: 2,
    help: 'Tokens spent on AI field normalisation, email analysis and LLM fallback. Not billed to your plan; it drives the estimated AI processing charges.',
  },
]

export default function MetricTiles({ metrics, selected, onSelect, chart, cycle }) {
  // The tiles always show the current billing month, whatever the Analysis
  // period control says, so they carry their own visible window label.
  const range = formatCycleLabel(cycle)
  return (
    <div>
      <p className="mb-2 text-[11px] font-medium text-gray-500 dark:text-gray-400">
        This billing month
        {range && (
          <>
            {' · '}
            <span title={billingMonthTooltip(cycle)} className="cursor-help underline decoration-dotted underline-offset-2">
              {range}
            </span>
          </>
        )}
        <span className="text-gray-400 dark:text-gray-500">, not the analysis period</span>
      </p>
      <div data-tour="metric-tiles" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {METRICS.map((metric, i) => {
          const data = metrics[metric.key] || {}
          const active = selected === metric.key
          const color = chart.series[metric.seriesIndex]
          const Icon = metric.icon

          return (
            // The help affordance is a sibling of the tile button, not a child of
            // it: a <button> inside a <button> is invalid HTML, and browsers
            // resolve it by swallowing the inner control.
            <div key={metric.key} className="relative animate-card-in" style={{ animationDelay: `${120 + i * 50}ms` }}>
              <span className="absolute top-3.5 right-3.5 z-10">
                <HelpTip label={`What does ${metric.label} count?`}>{metric.help}</HelpTip>
              </span>
              <button
              type="button"
              aria-pressed={active}
              onClick={() => onSelect(metric.key)}
              className={`group relative w-full text-left overflow-hidden rounded-xl border p-4 pb-0 bg-white dark:bg-gray-900
                transition-all duration-200
                focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500
                ${active
                  ? 'border-navy-600 dark:border-navy-400 shadow-sm ring-1 ring-navy-600/20 dark:ring-navy-400/20'
                  : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-md'}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{metric.label}</span>
              </div>

              <p className="mt-2">
                <CountUp
                  value={data.current || 0}
                  format={v => formatCompact(Math.round(v))}
                  className="text-[26px] font-bold text-gray-900 dark:text-gray-100 tracking-tight leading-none"
                />
              </p>

              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Delta value={data.change} isNew={data.isNew} inverse={metric.key === 'ai_tokens_used'} />
              </div>

              <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                {formatInt(data.allTime || 0)} all time
              </p>

              <div className="mt-2 -mx-4">
                <Sparkline data={data.history} color={color} />
              </div>

              <span
                className={`absolute inset-x-0 bottom-0 h-0.5 origin-left transition-transform duration-300
                            ${active ? 'scale-x-100' : 'scale-x-0'}`}
                style={{ backgroundColor: color }}
              />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
